import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/contexts/AuthContext";
import App from "./App";
import "./index.css";

// Remove initial loader once React takes over
const initialLoader = document.getElementById('initial-loader');
if (initialLoader) {
  initialLoader.remove();
}

// Initialize performance monitoring (deferred)
import('./lib/performanceMonitoring').then(({ observeWebVitals }) => {
  if ('requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(observeWebVitals);
  } else {
    setTimeout(observeWebVitals, 100);
  }
});

// Optimized QueryClient configuration for performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes - reduce refetches
      gcTime: 1000 * 60 * 30, // 30 minutes garbage collection
      retry: 1, // Single retry for faster failure
      refetchOnWindowFocus: false, // Disable focus refetch
      refetchOnMount: false, // Use cache on mount when available
      refetchOnReconnect: 'always', // But always refetch on reconnect
    },
  },
});

// Provider stack at TRUE root: QueryClient -> Auth -> App
createRoot(document.getElementById("root")!).render(
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <App />
    </AuthProvider>
  </QueryClientProvider>
);

// Register service worker for caching (production only)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // SW registration failed - non-critical
    });
  });
}
